import { LIFECYCLE_STATUS, type LifecycleStatus } from "../types/common";

interface SidebarFilterItem {
  label: string;
  /** Grouped key stored in the URL (?status=<key>) to drive sidebar selection UI. null = All. */
  status: string | null;
  /** Lifecycle status values sent to the API as repeated ?lifecycle_status= params. */
  lifecycleStatuses: LifecycleStatus[];
  default?: boolean;
}

/**
 * Vendor sees abstract grouped statuses.
 * Each item lists the exact lifecycle statuses it covers.
 */
const VENDOR_FILTERS: SidebarFilterItem[] = [
  { label: "All invoices", status: null, lifecycleStatuses: [], default: true },
  {
    label: "Pending submission",
    status: "pending_submission",
    lifecycleStatuses: [
      LIFECYCLE_STATUS.UPLOADED,
      LIFECYCLE_STATUS.OCR_IN_PROGRESS,
      LIFECYCLE_STATUS.VALIDATION_IN_PROGRESS,
    ],
  },
  {
    label: "Revision required - Vendor",
    status: "revision_required_vendor",
    lifecycleStatuses: [LIFECYCLE_STATUS.OCR_FAILED],
  },
  {
    label: "Submitted",
    status: "submitted",
    lifecycleStatuses: [
      LIFECYCLE_STATUS.VALIDATION_FAILED,
      LIFECYCLE_STATUS.GRN_PENDING,
      LIFECYCLE_STATUS.THREE_WAY_MATCH_PENDING,
      LIFECYCLE_STATUS.MATCH_EXCEPTION,
      LIFECYCLE_STATUS.UNDER_REVIEW,
    ],
  },
  {
    label: "Approved",
    status: "approved",
    lifecycleStatuses: [
      LIFECYCLE_STATUS.APPROVED,
      LIFECYCLE_STATUS.EXPORT_READY,
      LIFECYCLE_STATUS.SENT_TO_D365,
      LIFECYCLE_STATUS.D365_FAILED,
      LIFECYCLE_STATUS.EXPORTED,
    ],
  },
  {
    label: "Revision Required",
    status: "revision_required",
    lifecycleStatuses: [LIFECYCLE_STATUS.REJECTED],
  },
];

/**
 * Internal roles see grouped statuses with finer granularity than vendor.
 * Note: "approved" for internal excludes D365_FAILED/EXPORTED (which have their own groups).
 */
const INTERNAL_FILTERS: SidebarFilterItem[] = [
  { label: "All invoices", status: null, lifecycleStatuses: [], default: true },
  {
    label: "Pending submission",
    status: "pending_submission",
    lifecycleStatuses: [
      LIFECYCLE_STATUS.UPLOADED,
      LIFECYCLE_STATUS.OCR_IN_PROGRESS,
      LIFECYCLE_STATUS.VALIDATION_IN_PROGRESS,
    ],
  },
  {
    label: "Revision Required - Vendor",
    status: "revision_required_vendor",
    lifecycleStatuses: [LIFECYCLE_STATUS.OCR_FAILED],
  },
  {
    label: "Validation Failed - VM",
    status: "validation_failed_vm",
    lifecycleStatuses: [LIFECYCLE_STATUS.VALIDATION_FAILED],
  },
  {
    label: "GRN Pending",
    status: "grn_pending",
    lifecycleStatuses: [LIFECYCLE_STATUS.GRN_PENDING, LIFECYCLE_STATUS.THREE_WAY_MATCH_PENDING],
  },
  {
    label: "Validation failed",
    status: "validation_failed",
    lifecycleStatuses: [LIFECYCLE_STATUS.MATCH_EXCEPTION],
  },
  {
    label: "Finance Approval Pending",
    status: "finance_approval_pending",
    lifecycleStatuses: [LIFECYCLE_STATUS.UNDER_REVIEW],
  },
  {
    label: "Approved",
    status: "approved",
    lifecycleStatuses: [
      LIFECYCLE_STATUS.APPROVED,
      LIFECYCLE_STATUS.EXPORT_READY,
      LIFECYCLE_STATUS.SENT_TO_D365,
    ],
  },
  {
    label: "Rejected",
    status: "rejected",
    lifecycleStatuses: [LIFECYCLE_STATUS.REJECTED],
  },
  {
    label: "ERP upload Failed",
    status: "erp_upload_failed",
    lifecycleStatuses: [LIFECYCLE_STATUS.D365_FAILED],
  },
  {
    label: "ERP Success",
    status: "erp_success",
    lifecycleStatuses: [LIFECYCLE_STATUS.EXPORTED],
  },
];

export function getSidebarFilters(isInternal: boolean): SidebarFilterItem[] {
  return isInternal ? INTERNAL_FILTERS : VENDOR_FILTERS;
}

export function getDefaultFilterStatus(isInternal: boolean): string | null {
  const filters = getSidebarFilters(isInternal);
  return filters.find((f) => f.default)?.status ?? null;
}

/**
 * Number of invoices a sidebar filter covers, from the /summary response.
 * "All invoices" (or an unknown/missing filter) is the summary total; a grouped
 * status sums its lifecycle statuses over `status_counts`.
 *
 * Shared by the sidebar rows and the list's pagination total so the two can't drift.
 */
export function getFilterCount(
  filter: SidebarFilterItem | undefined,
  statusCounts: Record<string, number>,
  totalCount: number,
): number {
  if (!filter?.status) return totalCount;
  return filter.lifecycleStatuses.reduce((sum, ls) => sum + (statusCounts[ls] ?? 0), 0);
}

export type { SidebarFilterItem };
